// filepath: apps/web/src/features/admin/effects/queries.ts
//
// FR-UI-ADM-8 -- TanStack Query hooks for the outbound effect
// configuration admin editor.
//
// All hooks include `tenantSlug` in the cache key as the second
// segment (INV-1). Switching tenants does NOT leak the prior
// tenant's configuration data.
//
// INV-5: HTTP endpoint and email channel read-side responses
// MUST NOT carry `auth_secret_value` / `smtp_secret_value`.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError } from "@/definitions/types";
import { apiFetch } from "@/api";
import type {
  CreateEmailChannelRequest,
  CreateEmailChannelResponse,
  CreateHttpEndpointRequest,
  CreateHttpEndpointResponse,
  EmailChannel,
  HttpEndpoint,
  PatchStatusRequest,
  StagingOnlyError,
  VersionConflictError,
} from "./types";

export const effectsAdminKeys = {
  httpEndpoints: (tenantSlug: string) =>
    ["admin", tenantSlug, "effects", "http-endpoints"] as const,
  emailChannels: (tenantSlug: string) =>
    ["admin", tenantSlug, "effects", "email-channels"] as const,
  pendingReviews: (tenantSlug: string) =>
    ["admin", tenantSlug, "pending-reviews", "http_endpoint"] as const,
};

function classifyStagingOnlyError(_body: {
  error?: string;
  code?: string;
}): StagingOnlyError {
  return {
    kind: "staging_only",
    environmentKind: "production",
  };
}

// ─── HTTP Endpoints ──────────────────────────────────────────────────────────

export function useHttpEndpoints() {
  const { tenantSlug } = useTenant();
  return useQuery<{ endpoints: HttpEndpoint[] }, ApiError>({
    queryKey: effectsAdminKeys.httpEndpoints(tenantSlug),
    queryFn: async () => {
      const r = await apiFetch("/api/effects/http-endpoints");
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as { endpoints: HttpEndpoint[] };
    },
  });
}

export function useCreateHttpEndpoint() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    CreateHttpEndpointResponse,
    Error | VersionConflictError | StagingOnlyError,
    CreateHttpEndpointRequest
  >({
    mutationFn: async (req) => {
      const res = await apiFetch("/api/effects/http-endpoints", {
        method: "POST",
        init: {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(req),
        },
        idempotencyKey: true,
      });
      if (res.status === 403) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        if (body.code === "ENV_PRODUCTION") {
          throw classifyStagingOnlyError(body);
        }
      }
      if (res.status === 409) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
          current_version?: number;
          server_endpoint?: HttpEndpoint;
        };
        const err: VersionConflictError = {
          message: body.error ?? "version conflict",
          code: body.code ?? "HTTP_ENDPOINT_VERSION_MISMATCH",
          currentVersion: body.current_version ?? 0,
          serverEndpoint: body.server_endpoint,
        };
        throw err;
      }
      if (!res.ok) {
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
      }
      return (await res.json()) as CreateHttpEndpointResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: effectsAdminKeys.httpEndpoints(tenantSlug),
      });
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

export function usePatchHttpEndpoint() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    { endpoint: HttpEndpoint },
    Error | VersionConflictError | StagingOnlyError,
    { id: string; status: "active" | "disabled"; version: number }
  >({
    mutationFn: async ({ id, status, version }) => {
      const body: PatchStatusRequest = { status };
      const res = await apiFetch(`/api/effects/http-endpoints/${id}`, {
        method: "PATCH",
        init: {
          headers: {
            "Content-Type": "application/json",
            "If-Match": String(version),
          },
          body: JSON.stringify(body),
        },
        idempotencyKey: true,
      });
      if (res.status === 403) {
        const rb = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        if (rb.code === "ENV_PRODUCTION") throw classifyStagingOnlyError(rb);
      }
      if (res.status === 409) {
        const rb = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
          current_version?: number;
          server_endpoint?: HttpEndpoint;
        };
        const err: VersionConflictError = {
          message: rb.error ?? "version conflict",
          code: rb.code ?? "HTTP_ENDPOINT_VERSION_MISMATCH",
          currentVersion: rb.current_version ?? 0,
          serverEndpoint: rb.server_endpoint,
        };
        throw err;
      }
      if (!res.ok) {
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
      }
      return (await res.json()) as { endpoint: HttpEndpoint };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: effectsAdminKeys.httpEndpoints(tenantSlug),
      });
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

export function useDeleteHttpEndpoint() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error | VersionConflictError | StagingOnlyError,
    { id: string; version: number }
  >({
    mutationFn: async ({ id, version }) => {
      const res = await apiFetch(`/api/effects/http-endpoints/${id}`, {
        method: "DELETE",
        init: {
          headers: { "If-Match": String(version) },
        },
        idempotencyKey: true,
      });
      if (res.status === 403) {
        const rb = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        if (rb.code === "ENV_PRODUCTION") throw classifyStagingOnlyError(rb);
      }
      if (res.status === 409) {
        const rb = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
          current_version?: number;
          server_endpoint?: HttpEndpoint;
        };
        const err: VersionConflictError = {
          message: rb.error ?? "version conflict",
          code: rb.code ?? "HTTP_ENDPOINT_VERSION_MISMATCH",
          currentVersion: rb.current_version ?? 0,
          serverEndpoint: rb.server_endpoint,
        };
        throw err;
      }
      if (res.status !== 204 && !res.ok) {
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: effectsAdminKeys.httpEndpoints(tenantSlug),
      });
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

// ─── Email Channels ──────────────────────────────────────────────────────────

export function useEmailChannels() {
  const { tenantSlug } = useTenant();
  return useQuery<{ channels: EmailChannel[] }, ApiError>({
    queryKey: effectsAdminKeys.emailChannels(tenantSlug),
    queryFn: async () => {
      const r = await apiFetch("/api/effects/email-channels");
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as { channels: EmailChannel[] };
    },
  });
}

export function useCreateEmailChannel() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    CreateEmailChannelResponse,
    Error | VersionConflictError | StagingOnlyError,
    CreateEmailChannelRequest
  >({
    mutationFn: async (req) => {
      const res = await apiFetch("/api/effects/email-channels", {
        method: "POST",
        init: {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(req),
        },
        idempotencyKey: true,
      });
      if (res.status === 403) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        if (body.code === "ENV_PRODUCTION") {
          throw classifyStagingOnlyError(body);
        }
      }
      if (res.status === 409) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
          current_version?: number;
          server_channel?: EmailChannel;
        };
        const err: VersionConflictError = {
          message: body.error ?? "version conflict",
          code: body.code ?? "EMAIL_CHANNEL_VERSION_MISMATCH",
          currentVersion: body.current_version ?? 0,
          serverChannel: body.server_channel,
        };
        throw err;
      }
      if (!res.ok) {
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
      }
      return (await res.json()) as CreateEmailChannelResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: effectsAdminKeys.emailChannels(tenantSlug),
      });
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

export function usePatchEmailChannel() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    { channel: EmailChannel },
    Error | VersionConflictError | StagingOnlyError,
    { id: string; status: "active" | "disabled"; version: number }
  >({
    mutationFn: async ({ id, status, version }) => {
      const body: PatchStatusRequest = { status };
      const res = await apiFetch(`/api/effects/email-channels/${id}`, {
        method: "PATCH",
        init: {
          headers: {
            "Content-Type": "application/json",
            "If-Match": String(version),
          },
          body: JSON.stringify(body),
        },
        idempotencyKey: true,
      });
      if (res.status === 403) {
        const rb = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        if (rb.code === "ENV_PRODUCTION") throw classifyStagingOnlyError(rb);
      }
      if (res.status === 409) {
        const rb = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
          current_version?: number;
          server_channel?: EmailChannel;
        };
        const err: VersionConflictError = {
          message: rb.error ?? "version conflict",
          code: rb.code ?? "EMAIL_CHANNEL_VERSION_MISMATCH",
          currentVersion: rb.current_version ?? 0,
          serverChannel: rb.server_channel,
        };
        throw err;
      }
      if (!res.ok) {
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
      }
      return (await res.json()) as { channel: EmailChannel };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: effectsAdminKeys.emailChannels(tenantSlug),
      });
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

export function useDeleteEmailChannel() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error | VersionConflictError | StagingOnlyError,
    { id: string; version: number }
  >({
    mutationFn: async ({ id, version }) => {
      const res = await apiFetch(`/api/effects/email-channels/${id}`, {
        method: "DELETE",
        init: {
          headers: { "If-Match": String(version) },
        },
        idempotencyKey: true,
      });
      if (res.status === 403) {
        const rb = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        if (rb.code === "ENV_PRODUCTION") throw classifyStagingOnlyError(rb);
      }
      if (res.status === 409) {
        const rb = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
          current_version?: number;
          server_channel?: EmailChannel;
        };
        const err: VersionConflictError = {
          message: rb.error ?? "version conflict",
          code: rb.code ?? "EMAIL_CHANNEL_VERSION_MISMATCH",
          currentVersion: rb.current_version ?? 0,
          serverChannel: rb.server_channel,
        };
        throw err;
      }
      if (res.status !== 204 && !res.ok) {
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: effectsAdminKeys.emailChannels(tenantSlug),
      });
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}
